/**
 * The version row above the question box.
 *
 * The same row the reading pages show (docs/more-bible-translations.md, "Part C"): the
 * pinned versions (the original and the primary translation), which cannot be removed;
 * the added translations, each with an ✕; and an "Add a translation" select, grouped by
 * language. The row IS the set the next question reads. It is always visible once the
 * corpus is known, before the first answer too.
 *
 * Choosing adds — no Go button, nothing navigates — but only a choice the reader has
 * finished, the same rule as the reading page's select (`wireSelect` in
 * pipeline/read-alongside.js). A pick from the open list, by pointer, touch or keyboard, is
 * finished at once. Stepping a closed select with the arrow keys or a letter only moves the
 * choice, since each press changes its value; Enter adds it, and so does leaving the select.
 *
 * Its class names carry the `version-` prefix because `.picker` is already the
 * workspace's curation fieldset.
 */

import type { JSX, RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'
import { languageName, offerable } from './activeVersions'
import { t } from './i18n'

export interface PickableVersion {
  code: string
  name: string
  language: string
  /** Always in the row: no ✕, and never offered in the select. */
  pinned: boolean
}

export function VersionRow({
  versions,
  added,
  onAdd,
  onRemove,
}: {
  versions: PickableVersion[]
  /** The added codes, in the order they were added. */
  added: string[]
  /** One code at a time, never a whole list: a change made before the row is known waits for it. */
  onAdd: (code: string) => void
  onRemove: (code: string) => void
}): JSX.Element | null {
  const adder = useRef<HTMLSelectElement>(null)
  // A ✕ removes its own button, which would drop focus to the page body. It goes to the
  // select instead — after the render, because with everything added the select is not
  // there until the removal brings it back.
  const focusAdder = useRef(false)
  useEffect(() => {
    if (!focusAdder.current || adder.current === null) return
    focusAdder.current = false
    adder.current.focus()
  })
  if (versions.length === 0) return null
  const pinned = versions.filter((version) => version.pinned)
  const shown = added
    .map((code) => versions.find((version) => version.code === code))
    .filter((version): version is PickableVersion => version !== undefined)
  const groups = offerable(
    versions.filter((version) => !version.pinned),
    added,
  )

  return (
    <div className="version-row" role="group" aria-label={t('chat.versionRow')}>
      {pinned.map((version) => (
        // Named in full, as the reading page's tabs are: the visible code leads, the name follows unseen.
        <span key={version.code} className="version-row__tab" title={version.name}>
          {version.code}
          <span className="version-row__name">: {t('chat.pinnedVersion', { name: version.name })}</span>
        </span>
      ))}
      {shown.map((version) => (
        <span key={version.code} className="version-row__tab version-row__tab--added" title={version.name}>
          {version.code}
          <span className="version-row__name">: {version.name}</span>
          <button
            type="button"
            className="version-row__remove"
            aria-label={t('chat.removeVersion', { name: version.name })}
            title={t('chat.removeVersion', { name: version.name })}
            onClick={() => {
              focusAdder.current = true
              onRemove(version.code)
            }}
          >
            ✕
          </button>
        </span>
      ))}
      {groups.length > 0 && (
        <AddSelect groups={groups} adder={adder} onAdd={onAdd} />
      )}
    </div>
  )
}

// macOS opens a select's list on the arrow keys instead of stepping its value.
// The same test as the reading page (pipeline/read-alongside.js), so both surfaces agree.
const ARROWS_OPEN_LIST = /mac|iphone|ipad/i.test(navigator.platform)
const STEP_KEYS = ['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']
// Space and F4 open the list; so does Alt with an arrow.
const OPEN_KEYS = [' ', 'F4']
const ALT_OPEN_KEYS = ['ArrowDown', 'ArrowUp']

/**
 * What a key does to the select — `keyEffect` in pipeline/read-alongside.js, the same rule:
 * 'opens' its list (a pick from the open list is a finished choice, and while it is open the
 * select gets no key events, so the Enter that picks never reaches it); 'steps' a CLOSED
 * select's value, changing it on every press; or neither, which leaves the last answer standing.
 */
function keyEffect(event: { key: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean }): 'opens' | 'steps' | null {
  if (OPEN_KEYS.includes(event.key) || (event.altKey && ALT_OPEN_KEYS.includes(event.key))) return 'opens'
  if ([event.altKey, event.ctrlKey, event.metaKey].some(Boolean)) return null
  if (STEP_KEYS.includes(event.key)) return ARROWS_OPEN_LIST ? 'opens' : 'steps'
  // A printable character jumps to the next name starting with it.
  return event.key.length === 1 ? 'steps' : null
}

/** "Add a translation": a finished choice adds — nothing navigates, so there is no Go button. */
function AddSelect({
  groups,
  adder,
  onAdd,
}: {
  groups: { language: string; versions: PickableVersion[] }[]
  adder: RefObject<HTMLSelectElement | null>
  onAdd: (code: string) => void
}): JSX.Element {
  // The value the keys have stepped to, not yet added; '' when nothing is pending.
  const [pending, setPending] = useState('')
  // Whether the select's value is being stepped closed, so a change is not yet a choice.
  const stepping = useRef(false)
  const commit = (code: string): void => {
    setPending('')
    if (code !== '') onAdd(code)
  }
  return (
    <select
      ref={adder}
      className="version-row__add"
      aria-label={t('chat.addVersion')}
      value={pending}
      onPointerDown={() => {
        stepping.current = false
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit(event.currentTarget.value)
          return
        }
        const effect = keyEffect(event)
        if (effect !== null) stepping.current = effect === 'steps'
      }}
      onChange={(event) => {
        if (stepping.current) setPending(event.target.value)
        else commit(event.target.value)
      }}
      onBlur={() => {
        commit(pending)
      }}
    >
      <option value="">{t('chat.addVersion')}</option>
      {groups.map((group) => (
        <optgroup key={group.language} label={languageName(group.language)}>
          {group.versions.map((version) => (
            <option key={version.code} value={version.code}>
              {version.name} ({version.code})
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}
